import { TrashIcon } from '@heroicons/react/24/outline'

import { Spinner } from '@src/components/Spinner/Spinner'
import { useCart } from '@src/hooks/useCart'
import { useErrors } from '@src/hooks/useErrors'
import { useLoadings } from '@src/hooks/useLoadings'
import { useLocalizedRouter } from '@src/hooks/useLocalizedRouter'
import { useLocale } from '@src/i18n/LocaleProvider'
import { ServerActions } from '@src/services/server-actions.service'
import { useTrackRemoveFromCart } from '@src/utils/analytics.util'
import { CheckoutErrorsGroup } from '@src/utils/constants.util'
import { log } from '@src/utils/log.util'
import { cn } from '@src/utils/tailwind'

export function RemoveLineItemButton({
  lineItem,
  shouldRedirect = true,
  className,
}: {
  lineItem: Commercetools.LineItem
  className?: string
  shouldRedirect?: boolean
}) {
  const router = useLocalizedRouter()
  const trackRemoveFromCart = useTrackRemoveFromCart()
  const { t } = useLocale('checkout-page')
  const { loadings, setLoading } = useLoadings()
  const { setError } = useErrors()
  const { setCart, setShippingMethods } = useCart()

  const sku = lineItem.variant.sku ?? ''

  async function handleClick() {
    try {
      setLoading({
        removeLineItems: {
          [sku]: true,
        },
        getShippingMethods: true,
      })
      const cart = await ServerActions.serial.removeLineItems([
        { lineItemId: lineItem.id, quantity: lineItem.quantity },
      ])
      setCart(cart)
      await trackRemoveFromCart([lineItem])

      if (cart.lineItems.length === 0 && shouldRedirect) {
        router.push('/')
      } else {
        const shippingMethods = await ServerActions.serial.getShippingMethods()
        setShippingMethods(shippingMethods ?? [])
        setLoading({
          removeLineItems: {
            [sku]: false,
          },
          getShippingMethods: false,
        })
      }
    } catch (error: any) {
      log.error(`Error removing line item - ${error?.message}`, error)
      setError(error, { group: CheckoutErrorsGroup })
      setLoading({
        removeLineItems: {
          [sku]: false,
        },
        getShippingMethods: false,
      })
    }
  }

  return (
    <div className={cn('flex items-center justify-center', className)}>
      {loadings.removeLineItems?.[sku] ? (
        <Spinner size="smaller" className="-mb-1 w-4" />
      ) : (
        <TrashIcon
          data-testid="removeLineItemBtn"
          className="w-4 translate-y-[2px]"
          role="button"
          aria-label={t('remove-line-item')}
          tabIndex={0}
          onClick={() => {
            void handleClick()
          }}
          onKeyDown={(e) => {
            if (e.code === 'Enter' || e.code === 'Space') void handleClick()
          }}
        />
      )}
    </div>
  )
}
